/**
 * Folder-level Process configuration.
 *
 * Replaces the older "Diagram defaults" bundle. Users no longer pick a
 * coarse Purpose preset that secretly toggles five orthogonal axes —
 * they toggle each capability directly. The folder records:
 *
 *   - which *capability tracks* are on (controls/evidence as one, plus
 *     exceptions, tool instructions, automation, cost) — each maps 1:1
 *     to an `OPTIONAL_PROCESS_PACK` so the existing per-shape Properties
 *     panel keeps reading the same pack ids;
 *   - which compliance *frameworks* the folder is auditing against
 *     (relevant only when `tracksCompliance` is on);
 *   - which control-point *categories* the AI uses when proposing
 *     control mappings;
 *   - free-form *extraction notes* — a single textarea the user fills
 *     with anything they want the LLM to consider when reading their
 *     documents (terminology, sections to skip, owner-line conventions,
 *     numbering schemes, domain context). Injected verbatim into both
 *     Phase 1 and Phase 2 prompt preambles.
 *
 * Stored under `folder.metadata.processFolderConfig`. Missing key →
 * resolver returns empty defaults; a `processPurpose` preset (legacy
 * field on the same metadata bag) is consulted only when explicitly
 * applied via `applyQuickSetup`.
 */

import {
  getProcessPurposePreset,
  type ProcessPurposeId,
} from './processPurposePresets';

export const PROCESS_FOLDER_CONFIG_KEY = 'processFolderConfig' as const;

/** Soft cap on the free-form extraction notes — keeps prompt budget sane. */
export const EXTRACTION_NOTES_MAX_LENGTH = 2000;

export interface ProcessFolderConfig {
  /** Track controls + evidence + audit findings on each process. */
  tracksCompliance: boolean;
  /** Track exception handling, delegation, escalation. */
  tracksExceptions: boolean;
  /** Per-tool step-by-step instructions on each step. */
  tracksToolInstructions: boolean;
  /** Automation status + tooling per step. */
  tracksAutomation: boolean;
  /** Labor rates + fixed costs per step. */
  tracksCost: boolean;
  /** Frameworks the folder is auditing against (e.g. soc2, iso27001). */
  frameworks: string[];
  /** Control-point category ids the AI suggests when mapping controls. */
  controlCategories: string[];
  /**
   * Free-form notes the user wants the AI to consider during extraction.
   * Injected verbatim into Phase 1 + Phase 2 prompt preambles. Capped
   * at {@link EXTRACTION_NOTES_MAX_LENGTH} characters to keep prompt
   * budget bounded.
   */
  extractionNotes?: string;
}

const EMPTY_CONFIG: ProcessFolderConfig = {
  tracksCompliance: false,
  tracksExceptions: false,
  tracksToolInstructions: false,
  tracksAutomation: false,
  tracksCost: false,
  frameworks: [],
  controlCategories: [],
};

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    if (typeof item === 'string' && item.trim().length > 0) out.push(item);
  }
  return out;
}

function asBool(value: unknown): boolean {
  return value === true;
}

function asExtractionNotes(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  return trimmed.length > EXTRACTION_NOTES_MAX_LENGTH
    ? trimmed.slice(0, EXTRACTION_NOTES_MAX_LENGTH)
    : trimmed;
}

/**
 * Read the folder-level Process configuration. Returns null when the
 * folder has nothing stored yet — callers then render defaults.
 */
export function readProcessFolderConfig(
  metadata: Record<string, unknown> | null | undefined,
): ProcessFolderConfig | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const raw = (metadata as Record<string, unknown>)[PROCESS_FOLDER_CONFIG_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  return {
    tracksCompliance: asBool(data.tracksCompliance),
    tracksExceptions: asBool(data.tracksExceptions),
    tracksToolInstructions: asBool(data.tracksToolInstructions),
    tracksAutomation: asBool(data.tracksAutomation),
    tracksCost: asBool(data.tracksCost),
    frameworks: asStringArray(data.frameworks),
    controlCategories: asStringArray(data.controlCategories),
    extractionNotes: asExtractionNotes(data.extractionNotes),
  };
}

/**
 * Resolve a folder's effective Process configuration. Same as
 * `readProcessFolderConfig` but never returns null — empty config when
 * the folder has nothing stored.
 */
export function resolveProcessFolderConfig(
  metadata: Record<string, unknown> | null | undefined,
): ProcessFolderConfig {
  return readProcessFolderConfig(metadata) ?? EMPTY_CONFIG;
}

/**
 * Build a config from a Purpose preset. Used by the "Quick setup"
 * selector in the Settings tab — picking a preset writes these
 * capability flags wholesale; users can then deviate per toggle.
 */
export function configFromPurposePreset(
  purposeId: ProcessPurposeId | string | null | undefined,
): ProcessFolderConfig {
  const preset = getProcessPurposePreset(purposeId as ProcessPurposeId | null);
  if (!preset) return EMPTY_CONFIG;
  const packs = new Set(preset.defaultFieldPacks);
  return {
    tracksCompliance: packs.has('process-compliance'),
    tracksExceptions: packs.has('process-risk'),
    tracksToolInstructions: packs.has('process-tools'),
    tracksAutomation: packs.has('process-automation'),
    tracksCost: packs.has('process-cost'),
    frameworks: [...preset.defaultFrameworks],
    controlCategories: [...preset.defaultControlCategories],
  };
}

/**
 * Project the capability flags onto the canonical pack-id list that
 * downstream consumers (e.g. `ProcessBasicPropertiesTool`) still read
 * from. Keeps the pack-id contract stable so per-shape Properties keep
 * working without knowing about the new flag shape.
 */
export function enabledPacksFromConfig(config: ProcessFolderConfig): string[] {
  const out: string[] = [];
  if (config.tracksCompliance) out.push('process-compliance');
  if (config.tracksExceptions) out.push('process-risk');
  if (config.tracksToolInstructions) out.push('process-tools');
  if (config.tracksAutomation) out.push('process-automation');
  if (config.tracksCost) out.push('process-cost');
  return out;
}
